import {
  Constants,
  useMeeting,
  usePubSub,
  useTranscription,
} from "@videosdk.live/react-sdk";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { appThemes, useMeetingAppContext } from "../MeetingAppContextDef";
import { meetingModes } from "../CONSTS";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import { useMediaQuery } from "react-responsive";
import { Typography, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types"

// Helper function to get responsive padding
function getResponsivePadding(isMobile, isTab, type) {
  if (type === 'vertical') {
    if (isMobile) return "0.19vh";
    if (isTab) return "0.28vh";
    return "0.37vh";
  }
  // horizontal padding
  if (isMobile) return "0.37vh";
  if (isTab) return "0.56vh";
  return "0.74vh";
}

// Helper function to get responsive font size
function getResponsiveFontSize(isMobile, isTab) {
  if (isMobile) return "1.48vh";
  if (isTab) return "1.67vh";
  return "1.85vh";
}

// Helper function to get background color
function getCaptionBackgroundColor(appTheme, theme) {
  return appTheme === appThemes.LIGHT
    ? theme.palette.lightTheme.three
    : 'rgba(0, 0, 0, 0.1)';
}

function CaptionBox({ transcription, appTheme, theme, isMobile, isTab }) {
  if (!transcription) return null;

  const fontSize = getResponsiveFontSize(isMobile, isTab);
  const verticalPadding = getResponsivePadding(isMobile, isTab, 'vertical');
  const horizontalPadding = getResponsivePadding(isMobile, isTab, 'horizontal');
  const backgroundColor = getCaptionBackgroundColor(appTheme, theme);

  return (
    <div
      style={{
        display: "flex",
        marginBottom: "1.48vh",
      }}
    >
      <div
        style={{
          backgroundColor,
          paddingTop: verticalPadding,
          paddingBottom: verticalPadding,
          paddingLeft: horizontalPadding,
          paddingRight: horizontalPadding,
        }}
      >
        <strong
          style={{
            maxWidth: "10%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: " ellipsis",
            marginRight: "0.46vh",
            fontSize,
          }}
        >
          [ {transcription.participantName} ] :
        </strong>
        <span
          style={{
            fontSize,
          }}
        >
          {transcription.text}
        </span>
      </div>
    </div>
  );
}


CaptionBox.propTypes = {
  transcription: PropTypes.shape({
    text: PropTypes.string.isRequired,
    participantName: PropTypes.string.isRequired,
  }),
  appTheme: PropTypes.oneOf(Object.values(appThemes)).isRequired,
  theme: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isTab: PropTypes.bool.isRequired,
};

function getLeftPosition(isSideViewActive) {
  return isSideViewActive ? "0%" : "14%";
}

function getBottomPosition(isSideViewActive, recorder) {
  if (isSideViewActive) {
    return "15%";
  }
  if (recorder) {
    return "1%";
  }
  return "6%";
}

const RealTimeCaptionProvider = ({ isSideViewActive, isBannerActive }) => {
  const mMeeting = useMeeting({});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const recorder = queryParams.get("isRecorder");

  const [transcription, setTranscription] = useState();
  const {
    participantCanToggleRealtimeTranscription,
    notificationAlertsEnabled,
    meetingMode,
    appTheme,
  } = useMeetingAppContext();
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const theme = useTheme();
  const meetingModeRef = useRef(meetingMode);
  const { enqueueSnackbar } = useSnackbar();

  useTranscription({
    onTranscriptionStateChanged,
    onTranscriptionText,
  });

  function onTranscriptionStateChanged(data) {
    let status = data.status;
    if (
      participantCanToggleRealtimeTranscription &&
      notificationAlertsEnabled &&
      meetingModeRef.current === meetingModes.CONFERENCE &&
      (status === Constants.transcriptionEvents.TRANSCRIPTION_STARTED ||
        status === Constants.transcriptionEvents.TRANSCRIPTION_STOPPED)
    ) {
      enqueueSnackbar(
        status === Constants.transcriptionEvents.TRANSCRIPTION_STARTED
          ? "Meeting transcription is started."
          : "Meeting transcription is stopped."
      );
    }
  }
  function onTranscriptionText(payload) {
    if (payload && payload.text) {
      setTranscription({
        text: payload.text,
        participantName: payload.participantName,
      });
    }
  }

  useEffect(() => {
    if (
      mMeeting.transcriptionState ==
      Constants.transcriptionEvents.TRANSCRIPTION_STOPPED
    ) {
      setTranscription(null);
    }
  }, [mMeeting.transcriptionState]);

  return transcription ? (
    <div
      style={{
        position: "absolute",
        width: "70%",
        height: "auto",
        left: getLeftPosition(isSideViewActive),
        bottom: getBottomPosition(isSideViewActive, recorder),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "left",
        color: "white",
      }}
    >
      <Typography
        variant={"subtitle2"}
        style={{
          width: "70%",
          fontSize: "1.85vh",
          color: appTheme === appThemes.LIGHT && "#95959E",
        }}
      >
        <CaptionBox
          transcription={transcription}
          appTheme={appTheme}
          theme={theme}
          isMobile={isMobile}
          isTab={isTab}
        />
      </Typography>
    </div>
  ) : null;
};

RealTimeCaptionProvider.propTypes = {
  isSideViewActive: PropTypes.bool,
  isBannerActive: PropTypes.bool,
};

export default RealTimeCaptionProvider;
